import React, { useEffect, useState } from "react";
import styles from "./Residencies.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api/axois";
import { getFirstImageUrl } from "../../utils/imageUtils";
import Spinner from "../../components/Spinner";

const Residencies = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const [promo, setPromo] = useState(null);
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to preserve query parameters when navigating
  const handleRoomNavigation = (roomId) => {
    const queryString = location.search;
    navigate(`/rooms/${roomId}${queryString}`);
  };

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await api.get("/rooms");
        setRooms(res.data.rooms || res.data); // Adjust based on your API response
      } catch (err) {
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const promoCode = query.get("promo");
    if (!promoCode) {
      setPromo(null);
      setShowPromoPopup(false);
      return;
    }

    const fetchPromotion = async () => {
      try {
        const res = await api.get(`/promotions/resolve/${encodeURIComponent(promoCode)}`);
        const promotion = res.data?.promotion || null;
        if (promotion) {
          setPromo(promotion);
          setShowPromoPopup(true);
        }
      } catch (err) {
        setPromo(null);
        setShowPromoPopup(false);
      }
    };

    fetchPromotion();
  }, [location.search]);

  useEffect(() => {
    const timer = setTimeout(() => setShowSpinner(true), 600);
    return () => clearTimeout(timer);
  }, []);

  if (loading) return showSpinner ? <Spinner /> : <div style={{minHeight: 120}}></div>;

  return (
    <div className={styles.residenciesPage}>
      {showPromoPopup && promo && (
        <div className={styles.promoOverlay}>
          <div className={styles.promoModal}>
            <button
              type="button"
              className={styles.promoClose}
              onClick={() => setShowPromoPopup(false)}
              aria-label="Close promotion"
            >
              x
            </button>
            <p className={styles.promoTag}>Exclusive Offer</p>
            <h2 className={styles.promoTitle}>You get {promo.percentage}% off your stay</h2>
            <p className={styles.promoText}>
              Applicable room type: <strong>{promo.room_type_title || "Selected room"}</strong>
            </p>
            {(promo.criteria_display || promo.criteria) && (
              <p className={styles.promoText}>Criteria: {promo.criteria_display || promo.criteria}</p>
            )}
            <p className={styles.promoText}>
              Valid until {new Date(promo.ends_at).toLocaleString()}
            </p>
            <button type="button" className={styles.promoBtn} onClick={() => setShowPromoPopup(false)}>
              Continue to Residences
            </button>
          </div>
        </div>
      )}

      <section className={styles.introSection}>
        <h1 className={styles.residenciesHeading}>
          Exclusivity, elegance, and effortless sophistication.
        </h1>
        <p className={styles.residenciesDescription}>
          Choose your perfect stay with Aurelia! Whether you’re looking for a
          cozy retreat or extra space to share, we’ve got you covered.
        </p>
      </section>

      <section className={styles.apartmentOptions}>
        {rooms.map((room) => {
          const isUnavailable = room.available_count === 0 || room.is_available === false;
          const hasPromoForRoom = promo && Number(promo.room_type_id) === Number(room.id);

          const totalUnits = room.total_count || room.capacity || 0;
          const availableUnits = room.available_count !== undefined ? room.available_count : 0;
          return (
            <div className={styles.apartmentCard} key={room.id}>
              {hasPromoForRoom && (
                <div className={styles.promoBadge}>{promo.percentage}% OFF</div>
              )}
              <img
                src={getFirstImageUrl(room.images, "/default-room.jpg")}
                alt={room.title}
                className={styles.apartmentImg}
              />
              <div className={styles.apartmentContent}>
                <div className={styles.apartmentLeft}>
                  <h3 className={styles.apartmentTitle}>{room.title}</h3>
                  <p className={styles.apartmentSummary}>
                    {typeof room.description === 'string' && room.description.trim().length > 120
                      ? room.description.trim().substring(0,120) + '...'
                      : room.description}
                  </p>
                </div>
                <div className={styles.apartmentRight}>
                  <button
                    className={styles.bookBtn}
                    onClick={() => !isUnavailable && handleRoomNavigation(room.id)}
                    disabled={isUnavailable}
                    style={isUnavailable ? { background: '#ccc', cursor: 'not-allowed' } : {}}
                  >
                    {isUnavailable ? 'UNAVAILABLE' : 'BOOK NOW'}
                  </button>
                  <div>
                    <p className={styles.apartmentPrice}>
                      ₦{Number(room.price_per_night).toLocaleString()}/night
                    </p>
                    <p
                      className={styles.availability}
                      style={isUnavailable ? { color: 'red' } : { color: 'black' }}
                    >
                      {isUnavailable
                        ? 'Unavailable'
                        :  `AVAILABLE`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className={styles.testimonials}>
        <blockquote className={styles.testimonialLeft}>
          “Every detail at Aurelia exudes elegance. <br />
          From the moment I arrived, I felt like <br />
          royalty.”
          <br />
          <span>- Sophia (Aurelia Resident)</span>
        </blockquote>

        <blockquote className={styles.testimonialRight}>
          "Aurelia isn't just a place, it's an experience <br />
          of pure refinement and perfection."
          <br />
          <span>- James (Aurelia Resident)</span>
        </blockquote>

        <blockquote className={styles.testimonialLeft}>
          "The level of sophistication and attention <br />
          to detail is simply unmatched. Aurelia
          <br />
          sets a new standard for luxury."
          <br />
          <span>- Sadiq (Aurelia Resident)</span>
        </blockquote>
      </section>
    </div>
  );
};

export default Residencies;
